import React, { useEffect, useState } from 'react';
import './AIStrategyBuilder.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  generatePersonas,
  fetchStrategies,
  activateStrategy,
  deleteStrategy,
} from '../../../redux/actions/aiStrategyAction/AIStrategyAction';
import Loader from '../../commonComponents/Loader/Loader';
import PersonaCard from './PersonaCard';

const AIStrategyBuilder = () => {
  const dispatch = useDispatch();
  const { strategies, currentStrategy, isGeneratingPersonas } = useSelector(
    state => state.aiReducer
  );

  const [formData, setFormData] = useState({
    strategyName: '',
    domain: '',
    targetCompanyProfile: '',
    valueProposition: '',
    painPoints: '',
    goals: '',
  });

  const [showForm, setShowForm] = useState(true);
  const [viewMode, setViewMode] = useState('new'); // 'new' or 'saved'
  const [viewingStrategy, setViewingStrategy] = useState(null); // For viewing saved strategy details

  useEffect(() => {
    document.title = 'AI Strategy Builder';
    document.getElementsByClassName('common-area')?.[0]?.scrollTo(0, 0);
    dispatch(fetchStrategies(true)); // Load active strategies
  }, []);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = () => {
    if (!formData.strategyName || !formData.domain) {
      alert('Please fill in at least Strategy Name and Business Domain');
      return;
    }

    const businessContext = {
      strategyName: formData.strategyName,
      businessContext: {
        domain: formData.domain,
        targetCompanyProfile: formData.targetCompanyProfile,
        valueProposition: formData.valueProposition,
        painPoints: formData.painPoints.split(',').map(p => p.trim()),
        goals: formData.goals,
      },
    };

    dispatch(generatePersonas(businessContext));
    setShowForm(false);
  };

  const handleReset = () => {
    setFormData({
      strategyName: '',
      domain: '',
      targetCompanyProfile: '',
      valueProposition: '',
      painPoints: '',
      goals: '',
    });
    setShowForm(true);
  };

  const handleActivateStrategy = strategyId => {
    dispatch(activateStrategy(strategyId));
  };

  const handleDeleteStrategy = strategyId => {
    if (window.confirm('Are you sure you want to delete this strategy?')) {
      dispatch(deleteStrategy(strategyId));
      setViewingStrategy(null); // Close view if currently viewing
    }
  };

  const handleViewStrategy = strategy => {
    setViewingStrategy(strategy);
  };

  const handleCloseStrategyView = () => {
    setViewingStrategy(null);
  };

  return (
    <div className="ai-strategy-builder-container">
      <div className="strategy-header">
        <h1 className="page-title">AI Strategy Builder</h1>
        <p className="page-subtitle">
          Generate target personas using AI research to power your LinkedIn outreach
        </p>
      </div>

      <div className="view-mode-toggle">
        <button
          type="button"
          className={`toggle-btn ${viewMode === 'new' ? 'active' : ''}`}
          onClick={() => setViewMode('new')}
        >
          New Strategy
        </button>
        <button
          type="button"
          className={`toggle-btn ${viewMode === 'saved' ? 'active' : ''}`}
          onClick={() => setViewMode('saved')}
        >
          Saved Strategies ({strategies?.length || 0})
        </button>
      </div>

      {viewMode === 'new' ? (
        <>
          {showForm && !currentStrategy ? (
            <div className="strategy-form-container">
              <div className="form-group">
                <label htmlFor="strategyName">
                  Strategy Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="strategyName"
                  name="strategyName"
                  value={formData.strategyName}
                  onChange={handleInputChange}
                  placeholder="e.g., Q1 2024 Construction Outreach"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="domain">
                  Business Domain <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="domain"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  placeholder="e.g., B2B SaaS, Construction Tech, Healthcare IT"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="targetCompanyProfile">Target Company Profile</label>
                <input
                  type="text"
                  id="targetCompanyProfile"
                  name="targetCompanyProfile"
                  value={formData.targetCompanyProfile}
                  onChange={handleInputChange}
                  placeholder="e.g., Mid-market construction companies (50-200 employees)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="valueProposition">Value Proposition</label>
                <textarea
                  id="valueProposition"
                  name="valueProposition"
                  value={formData.valueProposition}
                  onChange={handleInputChange}
                  placeholder="What problem do you solve? What value do you deliver?"
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="painPoints">Pain Points (comma-separated)</label>
                <input
                  type="text"
                  id="painPoints"
                  name="painPoints"
                  value={formData.painPoints}
                  onChange={handleInputChange}
                  placeholder="e.g., Multi-site coordination, Field communication gaps"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="goals">Goals</label>
                <input
                  type="text"
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleInputChange}
                  placeholder="e.g., Book 10 demos this quarter"
                  className="form-input"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary generate-btn"
                  onClick={handleGenerate}
                  disabled={isGeneratingPersonas}
                >
                  {isGeneratingPersonas ? 'Generating...' : 'Generate Strategy'}
                </button>
              </div>
            </div>
          ) : null}

          {isGeneratingPersonas ? (
            <div className="generating-state">
              <Loader />
              <p className="generating-text">
                AI is researching your market and generating target personas...
              </p>
              <p className="generating-subtext">This usually takes 5-10 seconds</p>
            </div>
          ) : null}

          {!isGeneratingPersonas && currentStrategy && !showForm ? (
            <div className="personas-result-container">
              <div className="result-header">
                <div>
                  <h2 className="result-title">{currentStrategy.strategyName}</h2>
                  <p className="result-subtitle">
                    Generated {currentStrategy.personaCount} personas •{' '}
                    {new Date(currentStrategy.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button type="button" className="btn-secondary" onClick={handleReset}>
                  Generate New Strategy
                </button>
              </div>

              <div className="personas-grid">
                {currentStrategy.personas?.map((persona, index) => (
                  <PersonaCard
                    key={persona.personaName || `persona-${index}`}
                    persona={persona}
                    index={index}
                  />
                ))}
              </div>

              <div className="strategy-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => handleActivateStrategy(currentStrategy._id)}
                >
                  Save & Activate Strategy
                </button>
                <button type="button" className="btn-secondary" onClick={handleReset}>
                  Discard
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="saved-strategies-container">
          {viewingStrategy ? (
            <div className="strategy-detail-view">
              <div className="strategy-detail-header">
                <button type="button" className="btn-back" onClick={handleCloseStrategyView}>
                  ← Back to Strategies
                </button>
                <div className="strategy-detail-title-row">
                  <div>
                    <h2 className="strategy-detail-title">{viewingStrategy.strategyName}</h2>
                    <p className="strategy-detail-meta">
                      {viewingStrategy.personaCount} personas •{' '}
                      {viewingStrategy.businessContext?.domain || 'No domain specified'} •
                      Created {new Date(viewingStrategy.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="strategy-detail-actions">
                    {!viewingStrategy.isActive && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleActivateStrategy(viewingStrategy._id)}
                      >
                        Activate Strategy
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => handleDeleteStrategy(viewingStrategy._id)}
                    >
                      Delete Strategy
                    </button>
                  </div>
                </div>
              </div>

              {viewingStrategy.businessContext && (
                <div className="business-context-section">
                  <h3 className="section-title">Business Context</h3>
                  <div className="context-grid">
                    {viewingStrategy.businessContext.domain && (
                      <div className="context-item">
                        <strong>Domain:</strong> {viewingStrategy.businessContext.domain}
                      </div>
                    )}
                    {viewingStrategy.businessContext.targetCompanyProfile && (
                      <div className="context-item">
                        <strong>Target Companies:</strong>{' '}
                        {viewingStrategy.businessContext.targetCompanyProfile}
                      </div>
                    )}
                    {viewingStrategy.businessContext.valueProposition && (
                      <div className="context-item full-width">
                        <strong>Value Proposition:</strong>{' '}
                        {viewingStrategy.businessContext.valueProposition}
                      </div>
                    )}
                    {viewingStrategy.businessContext.goals && (
                      <div className="context-item">
                        <strong>Goals:</strong> {viewingStrategy.businessContext.goals}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="personas-detail-section">
                <h3 className="section-title">Target Personas</h3>
                <div className="personas-grid">
                  {viewingStrategy.personas?.map((persona, index) => (
                    <PersonaCard
                      key={persona.personaName || `persona-${index}`}
                      persona={persona}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {strategies && strategies.length > 0 ? (
                <div className="strategies-list">
                  {strategies.map(strategy => (
                    <div key={strategy._id} className="saved-strategy-card">
                      <div className="strategy-card-header">
                        <div>
                          <h3 className="strategy-card-title">{strategy.strategyName}</h3>
                          <p className="strategy-card-meta">
                            {strategy.personaCount} personas •{' '}
                            {strategy.businessContext?.domain || 'No domain specified'} •
                            {strategy.isActive ? (
                              <span className="active-badge">Active</span>
                            ) : (
                              <span className="inactive-badge">Inactive</span>
                            )}
                          </p>
                        </div>
                        <div className="strategy-card-actions">
                          <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={() => handleViewStrategy(strategy)}
                          >
                            View Details
                          </button>
                          {!strategy.isActive && (
                            <button
                              type="button"
                              className="btn-small btn-primary"
                              onClick={() => handleActivateStrategy(strategy._id)}
                            >
                              Activate
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteStrategy(strategy._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="personas-preview">
                        {strategy.personas?.slice(0, 2).map((persona) => (
                          <div key={persona.personaName} className="persona-preview-card">
                            <h4 className="persona-preview-title">{persona.personaName}</h4>
                            <p className="persona-preview-subtitle">
                              {persona.jobTitles?.slice(0, 2).join(', ')}
                            </p>
                          </div>
                        ))}
                        {strategy.personas?.length > 2 && (
                          <div className="persona-preview-more">
                            +{strategy.personas.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-strategies">
                  <p>No saved strategies yet</p>
                  <button type="button" className="btn-primary" onClick={() => setViewMode('new')}>
                    Create Your First Strategy
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIStrategyBuilder;
